import { useState, useEffect, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import Modal from '@components/ui/Modal/Modal';
import Button from '@components/ui/Button/Button';
import LogoSpinner from '@components/ui/LogoSpinner/LogoSpinner';
import Avatar from '@components/ui/Avatar/Avatar';
import SearchIcon from '@assets/icons/SearchIcon';
import { getTenants } from '@/services/tenantService';
import { initiateEntitySwitch } from '@/services/authService';
import { useTranslation } from '@/hooks/useTranslation';
import type { TenantWithUser } from '@/types/tenant';

import styles from './ClientSwitcherModal.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function ClientSwitcherModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<TenantWithUser | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input on mount (fires every open due to key-based remount in TopNav)
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Debounce: update debouncedSearch 300 ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['tenants', 'switcher', debouncedSearch],
    queryFn: ({ pageParam }) =>
      getTenants(
        { page: pageParam as number, size: 10 },
        {
          filters: { status: 'ACTIVE' },
          search: debouncedSearch.trim() || undefined,
        },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.last ? undefined : allPages.length,
    enabled: isOpen,
    staleTime: 30_000,
  });

  // Infinite scroll: fetch next page when sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const tenants = data?.pages.flatMap((p) => p.content) ?? [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setSelectedTenant(null);
  };

  const handleLogin = () => {
    if (!selectedTenant) return;
    onClose();
    initiateEntitySwitch(selectedTenant.tenant.subdomain);
  };

  const buttonLabel = selectedTenant
    ? t('K_BUTTON_LOG_IN_AS', 'Log in as {name}').replace('{name}', selectedTenant.tenant.name)
    : t('K_BUTTON_SWITCH_TO_CLIENT', 'Switch to Client');

  const footer = (
    <Button variant="filled" fullWidth onClick={handleLogin} disabled={!selectedTenant}>
      {buttonLabel}
    </Button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('K_MODAL_SWITCH_TO_CLIENT', 'Switch to Client')}
      size="sm"
      footer={footer}
    >
      <div className={styles.searchWrap}>
        <span className={styles.searchIcon}>
          <SearchIcon />
        </span>
        <input
          ref={searchInputRef}
          type="text"
          className={styles.searchInput}
          placeholder={t('K_PLACEHOLDER_SEARCH_CLIENTS', 'Search clients...')}
          value={search}
          onChange={handleSearchChange}
          aria-label={t('K_PLACEHOLDER_SEARCH_CLIENTS', 'Search clients...')}
        />
      </div>

      <div className={styles.list}>
        {isLoading && (
          <div className={styles.spinnerRow}>
            <LogoSpinner size="sm" />
            <p className={styles.loadingText}>{t('K_LOADING_CLIENTS', 'Loading clients…')}</p>
          </div>
        )}

        {!isLoading && tenants.length === 0 && (
          <p className={styles.empty}>{t('K_EMPTY_NO_ACTIVE_CLIENTS', 'No active clients found.')}</p>
        )}

        {tenants.map((item, index) => (
          <button
            key={item.tenant.id}
            type="button"
            className={`${styles.clientRow}${
              selectedTenant?.tenant.id === item.tenant.id ? ` ${styles.clientRowSelected}` : ''
            }${index > 0 ? ` ${styles.clientRowDivider}` : ''}`}
            onClick={() => setSelectedTenant(item)}
          >
            <Avatar name={item.tenant.name} size="sm" />
            <div className={styles.clientInfo}>
              <span className={styles.clientName}>{item.tenant.name}</span>
              <span className={styles.clientSubdomain}>{item.tenant.subdomain}</span>
            </div>
          </button>
        ))}

        {isFetchingNextPage && (
          <div className={styles.spinnerRow}>
            <LogoSpinner size="sm" />
          </div>
        )}

        {/* Sentinel: IntersectionObserver fires fetchNextPage when this enters the viewport */}
        <div ref={sentinelRef} />
      </div>
    </Modal>
  );
}
